import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://terview.ai';
const DEFAULT_IMAGE = `${SITE}/android-chrome-512x512.png`;

/**
 * Per-page SEO tags (title, description, canonical, Open Graph, Twitter, and
 * optional JSON-LD). Drop <Seo ... /> at the top of any page component.
 *
 * Props:
 *   title       - page <title> (a " | Terview AI" suffix is added unless the
 *                 title already contains "Terview")
 *   description - meta description
 *   path        - route path for the canonical URL, e.g. "/resume-builder"
 *   image       - absolute OG/Twitter image URL (defaults to the app icon)
 *   noindex     - if true, adds <meta name="robots" content="noindex">
 *   jsonLd      - optional object (or array) rendered as application/ld+json
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd,
}) {
  const fullTitle =
    title && !/terview/i.test(title) ? `${title} | Terview AI` : title;
  const canonical = `${SITE}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {blocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
